import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStore } from "@/contexts/StoreContext";
import { bumpProjectDetailsTimestamp, pickNewerProjectDetails } from "@/services/projectDetails";
import type { ProjectDetailsResponse } from "@/types/project";

export type ProjectDetailsPatch = (details: ProjectDetailsResponse) => ProjectDetailsResponse;

/**
 * Applies a pure `ProjectDetailsResponse` patch (see `services/projectCachePatch.ts`)
 * to both the React Query `["project", id]` cache and the MobX `actionStore.projectDetails`
 * - the same dual-write `useOptimisticProjectSave` already does after a whole-project save -
 * so a v2 per-entity mutation's result is visible immediately, without a full project refetch.
 *
 * A no-op if neither cache currently holds that project (nothing to patch yet).
 */
export function useProjectCachePatch() {
  const queryClient = useQueryClient();
  const { actionStore } = useStore();

  return useCallback(
    (projectId: string, patch: ProjectDetailsPatch) => {
      queryClient.setQueryData<ProjectDetailsResponse>(["project", projectId], (old) => {
        if (!old) {
          return old;
        }

        const patched = bumpProjectDetailsTimestamp(patch(old));
        return pickNewerProjectDetails(old, patched) ?? patched;
      });

      if (actionStore.projectDetails?.project_id === projectId) {
        actionStore.setProjectDetails(bumpProjectDetailsTimestamp(patch(actionStore.projectDetails)));
      }
    },
    [queryClient, actionStore]
  );
}
